/**
 * PlanStateBar — across the top of every plan page: which of the four states
 * the plan is in (migration 919, owner ruling 2026-10-15).
 *
 *   Being prepared → Authorized, starts later → In force → Replaced
 *
 * The current step is solid; steps it has passed are outlined with a check;
 * steps still ahead are dashed. Under each step, the date that belongs to it.
 * Whoever may authorize the plan can set or change its start date, or make it take
 * effect today, here. An authorized plan with no start date reads "Start date pending" (922).
 *
 * Until migration 919 is applied the state cannot be read, and the page shows
 * the earlier band (RankModeBand) instead — a bar that guesses is worse than none.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TextInput } from '@/components/ds';
import { btnPrimary, btnSecondary, LABEL_MONO } from '@/components/mono/primitives';
import { RankModeBand } from '@/components/prioritization/RankModeBand';
import { formatDate } from '@/i18n/formatters';
import { usePlanApprover } from '@/hooks/prioritization/usePlanApprover';
import { fetchPlanForceRow, PLAN_FORCE_STEPS, setPlanScope, setPlanTakesEffect, type PlanForceRow } from '@/lib/planForceService';
import { getTypedSupabaseClient } from '@/lib/typedSupabaseClient';
import { ChoiceList } from '@/components/shared/ChoiceList';

const d = (iso: string | null | undefined) => (iso ? formatDate(iso, { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function sentence(r: PlanForceRow): string {
  switch (r.state) {
    case 'being_prepared':
      return `Being prepared. Its ranks are not of record yet; they go on record once it is authorized and its start date comes${r.takesEffectOn ? ` (starts on ${d(r.takesEffectOn)})` : ''}.`;
    case 'starts_later':
      return r.takesEffectOn
        ? `Authorized ${d(r.authorizedAt)}. Starts on ${d(r.takesEffectOn)}; until then the plan in force now stays in force.`
        : `Authorized ${d(r.authorizedAt)}. Start date pending: it is not in force until someone who may authorize it sets a start date or makes it take effect today.`;
    case 'in_force':
      return `In force since ${d(r.effectiveOn)}. Its ranks are of record. A later plan replaces it on the day that plan starts.`;
    case 'replaced':
      return 'Replaced by a later plan. Its ranks read as replaced; nothing was deleted.';
  }
}

export function PlanStateBar({ planCycleId }: { planCycleId: string }) {
  const [row, setRow] = useState<PlanForceRow | null | 'unavailable'>(null);
  const mayChange = usePlanApprover(planCycleId) === true;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Org nodes that may have their own plan in force (besides the top). None: every plan is the whole organization's.
  const [nodes, setNodes] = useState<{ id: string; name: string }[]>([]);
  const [pickingScope, setPickingScope] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      const db = getTypedSupabaseClient();
      const { data: plan } = await db.from('plan_cycles').select('workspace_id').eq('id', planCycleId).maybeSingle();
      if (!plan?.workspace_id) return;
      const { data } = await db
        .from('org_units')
        .select('id, name, display_name')
        .eq('workspace_id', plan.workspace_id)
        .eq('own_plan_in_force', true)
        .is('retired_at', null);
      if (live) setNodes((data ?? []).map((n) => ({ id: n.id, name: n.display_name || n.name })));
    })();
    return () => {
      live = false;
    };
  }, [planCycleId]);

  // Read in the plan's own workspace, not whichever workspace is open.
  const load = useCallback(async () => {
    const mine = await fetchPlanForceRow(planCycleId);
    setRow(mine ?? 'unavailable');
  }, [planCycleId]);

  useEffect(() => {
    setRow(null);
    void load();
  }, [planCycleId, load]);

  if (row === null) return null;
  if (row === 'unavailable') return <RankModeBand planCycleId={planCycleId} />;

  const at = PLAN_FORCE_STEPS.findIndex((s) => s.state === row.state);
  const dateFor = (state: string): string => {
    if (state === 'being_prepared') return row.state === 'being_prepared' ? 'Not yet authorized' : '';
    if (state === 'starts_later') {
      const start = row.takesEffectOn ? `starts on ${d(row.takesEffectOn)}` : 'start date pending';
      if (!row.authorizedAt) return row.takesEffectOn ? `Starts on ${d(row.takesEffectOn)}` : '';
      return `Authorized ${d(row.authorizedAt)} · ${start}`;
    }
    if (state === 'in_force') {
      if (row.state === 'in_force' || row.state === 'replaced') return `Since ${d(row.effectiveOn)}`;
      return row.takesEffectOn ? `From ${d(row.takesEffectOn)}` : 'Start date pending';
    }
    return '';
  };

  const save = async (value: string | null) => {
    setSaving(true);
    setError(null);
    const err = await setPlanTakesEffect(planCycleId, value);
    setSaving(false);
    if (err) {
      setError(err);
      return;
    }
    setEditing(false);
    await load();
  };

  return (
    <section
      role="status"
      aria-label="Plan state"
      data-plan-state={row.state}
      className="border px-4 py-3"
      style={{ borderColor: 'var(--rule-bright)', background: 'var(--bg-raised)' }}
    >
      <ol className="grid grid-cols-2 gap-x-3 gap-y-3 sm:grid-cols-4">
        {PLAN_FORCE_STEPS.map((s, i) => {
          const current = i === at;
          const passed = i < at;
          return (
            <li key={s.state} className="flex min-w-0 items-start gap-2" aria-current={current ? 'step' : undefined}>
              <span
                aria-hidden
                className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border text-[10px] leading-none"
                style={{
                  borderColor: current || passed ? 'var(--slate)' : 'var(--rule-bright)',
                  borderStyle: current || passed ? 'solid' : 'dashed',
                  background: current ? 'var(--slate)' : 'transparent',
                  color: current ? 'white' : 'var(--text-sec)',
                }}
              >
                {passed ? '✓' : ''}
              </span>
              <span className="min-w-0">
                <span
                  className="block font-mono text-[11px] uppercase tracking-[0.08em]"
                  style={{ color: current ? 'var(--text-pri)' : 'var(--text-ter)', fontWeight: current ? 600 : 400 }}
                >
                  {s.label}
                </span>
                <span className="block text-[13px] text-[var(--text-sec)]">
                  {s.state === 'replaced' && row.state === 'replaced' && row.replacedBy ? (
                    <Link to={`/planning/plans/${row.replacedBy}`} className="underline underline-offset-2 hover:text-[var(--text-pri)]">
                      See the plan in force
                    </Link>
                  ) : (
                    dateFor(s.state)
                  )}
                </span>
              </span>
            </li>
          );
        })}
      </ol>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t pt-2.5" style={{ borderColor: 'var(--rule)' }}>
        <p className="text-sm text-[var(--text-sec)]">{sentence(row)}</p>
        {mayChange && row.state !== 'replaced' && !editing && (
          <span className="flex flex-wrap gap-2">
          {row.state === 'starts_later' && (
            <button {...btnPrimary()} disabled={saving} onClick={() => void save(new Date().toISOString().slice(0, 10))}>
              Take effect today
            </button>
          )}
          <button
            {...btnSecondary()}
            onClick={() => {
              setDraft(row.takesEffectOn ?? '');
              setError(null);
              setEditing(true);
            }}
          >
            {row.takesEffectOn ? 'Change the start date' : 'Set the start date'}
          </button>
          </span>
        )}
      </div>

      {nodes.length > 0 && (
        <div className="mt-2 text-sm text-[var(--text-sec)]">
          <span className="text-[var(--text-ter)]">Whose plan: </span>
          {row.scopeOrgUnitId ? nodes.find((n) => n.id === row.scopeOrgUnitId)?.name ?? 'A node not listed here' : 'The whole organization'}
          {mayChange && row.state !== 'replaced' && !pickingScope && (
            <button {...btnSecondary('ml-3 !h-7 px-2.5')} onClick={() => setPickingScope(true)}>
              Change
            </button>
          )}
          {pickingScope && (
            <div className="mt-2">
              <ChoiceList
                label="Whose plan"
                stacked
                value={row.scopeOrgUnitId ?? ''}
                disabled={saving}
                options={[{ value: '', label: 'The whole organization' }, ...nodes.map((n) => ({ value: n.id, label: n.name }))]}
                onPick={async (v) => {
                  setSaving(true);
                  setError(null);
                  const err = await setPlanScope(planCycleId, v || null);
                  setSaving(false);
                  if (err) setError(err);
                  else {
                    setPickingScope(false);
                    await load();
                  }
                }}
              />
              <button {...btnSecondary('mt-2')} onClick={() => setPickingScope(false)}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {error && !editing && <p className="mt-2 text-sm text-[var(--coral-text)]">{error}</p>}

      {editing && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="min-w-[12rem]">
            <span className={LABEL_MONO}>Starts on</span>
            <TextInput type="date" aria-label="Starts on" value={draft} onChange={(e) => setDraft(e.target.value)} />
          </label>
          <button {...btnPrimary()} disabled={saving || !draft} onClick={() => void save(draft)}>
            {saving ? 'Saving…' : 'Save date'}
          </button>
          {row.takesEffectOn && (
            <button {...btnSecondary()} disabled={saving} onClick={() => void save(null)} title="Clear the date; its start date is pending">
              Clear the date
            </button>
          )}
          <button {...btnSecondary()} disabled={saving} onClick={() => setEditing(false)}>
            Cancel
          </button>
          {error && <p className="basis-full text-sm text-[var(--coral-text)]">{error}</p>}
        </div>
      )}
    </section>
  );
}

export default PlanStateBar;
